import type { TranslateFn } from '@/i18n/useTimePilotT';

/**
 * Helpers for the preliminary-message payload format used to surface
 * intermediate "thinking" / tool-progress strings to the chat UI while the
 * agent is still streaming.
 *
 * Two on-the-wire formats exist:
 *   - Legacy:  `PRELIMINARY_MESSAGES|<msg1>\n<msg2>\n...`
 *   - Current: `PRELIMINARY_MESSAGES_JSON|<JSON.stringify(string[])>`
 *
 * The JSON form is preferred because individual messages can themselves
 * contain newlines (the agent emits multi-line Python comments). The legacy
 * format is still parsed so cached / in-flight rows from older code paths
 * continue to render.
 */

export const FETCHING_MESSAGE = 'Retrieving answer...';
export const PROCESSING_MESSAGE = 'Processing...';
export const PRELIMINARY_MESSAGES_PREFIX = 'PRELIMINARY_MESSAGES|';
export const PRELIMINARY_MESSAGES_JSON_PREFIX = 'PRELIMINARY_MESSAGES_JSON|';

export function isPreliminaryMessagesText(text: string | null | undefined): boolean {
  if (!text) return false;
  return (
    text.startsWith(PRELIMINARY_MESSAGES_JSON_PREFIX) ||
    text.startsWith(PRELIMINARY_MESSAGES_PREFIX)
  );
}

export function encodePreliminaryMessageStrings(messages: readonly string[]): string {
  const contents = messages.filter(
    (content): content is string => typeof content === 'string' && content.length > 0,
  );
  return `${PRELIMINARY_MESSAGES_JSON_PREFIX}${JSON.stringify(contents)}`;
}

export function encodePreliminaryMessages(
  messages: ReadonlyArray<{ content?: string | null }>,
): string {
  return encodePreliminaryMessageStrings(
    messages
      .map((message) => message.content)
      .filter((content): content is string => typeof content === 'string'),
  );
}

export function parsePreliminaryMessages(
  text: string | null | undefined,
  t?: TranslateFn,
): string[] {
  const fetchingMessage = t?.('chat.progress.retrieving') ?? FETCHING_MESSAGE;
  const processingMessage = t?.('chat.progress.processing') ?? PROCESSING_MESSAGE;
  if (!text || text.trim() === '') {
    return [fetchingMessage];
  }

  if (text.startsWith(PRELIMINARY_MESSAGES_JSON_PREFIX)) {
    try {
      const parsed = JSON.parse(text.slice(PRELIMINARY_MESSAGES_JSON_PREFIX.length));
      if (Array.isArray(parsed)) {
        const filtered = parsed.filter(
          (message): message is string => typeof message === 'string' && message.length > 0,
        );
        return filtered.length > 0 ? filtered : [processingMessage];
      }
    } catch {
      // Fall through to the catch-all below.
    }
    return [processingMessage];
  }

  if (text.startsWith(PRELIMINARY_MESSAGES_PREFIX)) {
    return text.slice(PRELIMINARY_MESSAGES_PREFIX.length).split('\n');
  }

  return [text];
}
